import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "@/components/Editor/RichTextEditor";
import { RichTextPreview } from "@/components/Editor/RichTextPreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Eye, Edit3 } from "lucide-react";
import {
  adminAgencyBlogService,
  type AgencyBlogResponseDto,
  type CreateAgencyBlogInput,
} from "@/services/admin/agencyBlog.admin.service";
import {
  checkRichTextLength,
  getRichTextLimitError,
} from "@/utils/richText";

interface AgencyBlogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  blog?: AgencyBlogResponseDto | null;
  mode: "create" | "edit";
}

export function AgencyBlogModal({
  isOpen,
  onClose,
  onSuccess,
  blog,
  mode,
}: AgencyBlogModalProps) {
  const [formData, setFormData] = useState<CreateAgencyBlogInput>({
    title: "",
    description: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");

  useEffect(() => {
    if (mode === "edit" && blog) {
      setFormData({
        title: blog.title,
        description: blog.description,
      });
    } else {
      setFormData({
        title: "",
        description: "",
      });
    }
    setActiveTab("edit");
  }, [mode, blog, isOpen]);

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }

    const descriptionError = getRichTextLimitError(formData.description, 7000);
    if (descriptionError) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === "create") {
        await adminAgencyBlogService.create(formData);
      } else if (mode === "edit" && blog) {
        await adminAgencyBlogService.update(blog.id, formData);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving blog:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ title: "", description: "" });
    setActiveTab("edit");
    onClose();
  };

  const descriptionLimit = checkRichTextLength(formData.description, 7000);
  const isFormValid =
    !!formData.title.trim() &&
    !!formData.description.trim() &&
    !descriptionLimit.isOver;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <h6>
            {mode === "create" ? "Create New Blog Post" : "Edit Blog Post"}
          </h6>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as "edit" | "preview")}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="edit" className="flex items-center gap-2">
              <Edit3 size={16} />
              Editor Tab
            </TabsTrigger>
            <TabsTrigger value="preview" className="flex items-center gap-2">
              <Eye size={16} />
              Preview Tab
            </TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Enter blog title..."
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Content</Label>
              <RichTextEditor
                content={formData.description}
                onChange={(content) =>
                  setFormData((prev) => ({ ...prev, description: content }))
                }
                placeholder="Write your blog content here..."
                maxLength={7000}
              />
              {descriptionLimit.isOver && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {getRichTextLimitError(formData.description, 7000)}
                </p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="preview" className="space-y-4">
            <div className="space-y-4">
              <div className="border-b pb-4">
                <h2 className="text-2xl font-bold text-foreground">
                  {formData.title || "Untitled"}
                </h2>
              </div>
              <div className="prose max-w-none">
                <RichTextPreview content={formData.description} />
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="bg-[#d7785b] hover:bg-[#d7785b]/80"
          >
            {isSubmitting
              ? mode === "create"
                ? "Creating..."
                : "Updating..."
              : mode === "create"
              ? "Create Blog"
              : "Update Blog"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
